'use client';
import PvzMapDetailsBody from '@/entities/PvzMapDetailsBody/ui/PvzMapDetailsBody';
import PvzMapDetailsHeader from '@/entities/PvzMapDetailsHeader/ui/PvzMapDetailsHeader';
import PvzMapItem from '@/entities/PvzMapItem/ui/PvzMapItem';
import PvzMapListItems from '@/entities/PvzMapListItems/ui/PvzMapListItems';
import loadYmaps from '@/features/loadYMaps';
import { IBasketItem } from '@/shared/api/getBasket/types';
import { IDeliveryPoint } from '@/shared/api/postDeliveryBoxberryPoints/types';
import { useMediaValues } from '@/shared/hooks/useMediaValues';
import useCoefficientRem from '@/shared/hooks/useRem';
import { useCartStore } from '@/shared/store/cartStore';
import { useCityStore } from '@/shared/store/cityStore';
import { Button } from '@/shared/ui/Button';
import LoadingSpinner from '@/shared/ui/LoadingSpinner/LoadingSpinner';
import { getDateAfterDaysFormatted } from '@/shared/utils/getDateAfterDaysFormatted';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import ChooseCity from '@/widgets/ChooseCity/ui/ChooseCity';
import { initClusterer } from '@/widgets/PvzMap/utils/initClusterer';
import { cvaCloseWrapper } from '@/widgets/StoresMap/ui/StoresMapStyles';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock-upgrade';
import { motion } from 'framer-motion';
import { useLenis } from 'lenis/react';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BottomSheet, BottomSheetRef } from 'react-spring-bottom-sheet';
import { useDebounce, useMedia, useMount } from 'react-use';
import { useShallow } from 'zustand/react/shallow';
import {
  cvaAside,
  cvaAsideButton,
  cvaAsideButtonWrapper,
  cvaAsideHeader,
  cvaAsideHeaderActiveCity,
  cvaAsideHeaderTitle,
  cvaAsideItems,
  cvaAsideScroll,
  cvaBack,
  cvaBackIcon,
  cvaBottomSheetHeader,
  cvaClose,
  cvaCloseIcon,
  cvaLoadingContainer,
  cvaMap,
  cvaMapWrapper,
  cvaRoot,
  cvaSearch,
  cvaSearchIcon,
  cvaSearchInput,
  cvaSheet,
  cvaStoreDetails,
  cvaTag,
  cvaTags,
  cvaZoom,
  cvaZoomButton,
  cvaZoomButtonIcon,
} from './PvzMapStyles';
import HeaderShops from './shops/header';
import IconArrowLeft from '/public/icons/arrow-right-2.svg';
import IconClose from '/public/icons/close.svg';
import IconMinus from '/public/icons/minus.svg';
import IconPlus from '/public/icons/plus2.svg';
import IconSearch from '/public/icons/search3.svg';

// const MIN_HEIGHT_BOTTOM_SHEET = 200;
// const MIN_HEIGHT_BOTTOM_SHEET_MOBILE = 350;

interface Props {
  onClose: () => void;
  activeCity: string;
  shops: IDeliveryPoint[];
  itemsCount: number;
  allItems: number;
  pickupItems: IBasketItem[];
  onStoreChange?: (value: string) => void;
  defaultProduct?: any;
}

const PvzMap: FC<Props> = ({
  onClose: onCloseOrigin,
  activeCity,
  shops,
  itemsCount,
  allItems,
  pickupItems,
  onStoreChange,
  defaultProduct,
}) => {
  const [sheetVisible, setSheetVisible] = useState(false);

  const onClose = useCallback(() => {
    setSheetVisible(false);
    onCloseOrigin();
  }, [onCloseOrigin]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const clustererRef = useRef<any>(null);
  const zoomInRef = useRef<HTMLDivElement>(null);
  const zoomOutRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<BottomSheetRef>(null);
  const [didMount, setDidMount] = useState<boolean>(false);
  const coefficientRem = useCoefficientRem();

  // if city name changed but shops are from another city then new shops are loading
  const isShopsLoading = shops[0]?.address.split(',')[0].toLowerCase() !== activeCity.toLowerCase();

  const [showChooseCityPopup, setShowChooseCityPopup] = useState<boolean>(false);

  const [hideChooseCityControls, setHideChooseCityControls] = useState<boolean>(false);

  const [showBackArrow, setShowBackArrow] = useState<boolean>(false);
  const [stateBottomSheet, setStateBottomSheet] = useState<'top' | 'bottom'>('bottom');

  useMount(() => {
    setDidMount(true);
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const [pvzFilter, setPvzFilter] = useState<'all' | 'pvz' | 'postamat'>('all');

  const tariff = useCartStore((state) => state.cdekTariff);

  const [addressSearchQuery, setAddressSearchQuery] = useState('');
  const [addressSearchQueryDebounced, setAddressSearchQueryDebounced] = useState('');
  const [searchedAddresses, setSearchedAddresses] = useState<any[]>([]);

  const [] = useDebounce(
    async () => {
      setAddressSearchQueryDebounced(addressSearchQuery);
    },
    300,
    [addressSearchQuery]
  );

  useEffect(() => {
    if (addressSearchQueryDebounced) {
      const filteredAddresses = shops
        ?.filter((i) => i.address.toLowerCase().indexOf(addressSearchQueryDebounced.toLowerCase()) !== -1)
        .filter((shop) => shop.address.toLowerCase().includes(addressSearchQuery.toLowerCase()))
        .slice(0, 10);
      setSearchedAddresses(filteredAddresses);
    } else {
      setSearchedAddresses([]);
    }
  }, [addressSearchQueryDebounced]);

  const isMd = useMedia('(max-width: 1023px)');
  const rootLenis = useLenis()!;

  const [mapReady, setMapReady] = useState<boolean>(false);
  const [showPvzDetails, setShowPvzDetails] = useState<boolean>(false);
  const [tempActivePvz, setTempActivePvz] = useState<IDeliveryPoint | null>(null);

  const setActivePvz = useCartStore((store) => store.setActivePvz);

  /* Центр карты. По приоритетности: координаты города -> координаты первого ПВЗ */
  const activeMapCenter = useCityStore(
    useShallow((state) => {
      // координаты города
      return (
        state.cities
          .find((i) => i.code === state.city)
          ?.coordinates?.split(',')
          ?.map((i) => Number.parseFloat(i)) || [
          // координаты первого ПВЗ
          shops[0]?.latitude,
          shops[0]?.longitude,
        ]
      );
    })
  );

  useEffect(() => {
    loadYmaps(() => {
      if ((window as any).ymaps) {
        (window as any).ymaps.ready().then(() => {
          initMap();
          setMapReady(true);
        });
      }
    });

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
      }
    };
  }, []);

  const initMap = () => {
    mapInstanceRef.current = new (window as any).ymaps.Map(
      mapRef.current,
      {
        center: activeMapCenter,
        zoom: 10,
        controls: [],
      },
      {
        suppressMapOpenBlock: true,
        yandexMapDisablePoiInteractivity: true,
        minZoom: 3,
      }
    );

    // add zoomIn zoomOut events
    if (zoomInRef?.current) {
      zoomInRef?.current?.addEventListener('click', () => {
        mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom() + 1, {
          duration: 300,
        });
      });
    }
    if (zoomOutRef.current) {
      zoomOutRef.current.addEventListener('click', () => {
        mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom() - 1, {
          duration: 300,
        });
      });
    }
  };

  useEffect(() => {
    /* Обновляем центр карты после смены города */
    if (mapReady && mapInstanceRef.current && activeMapCenter) {
      mapInstanceRef.current.setCenter(activeMapCenter);
    }
  }, [mapReady, activeMapCenter]);

  useEffect(() => {
    /* Обновляем кластер при изменении списка ПВЗ */
    if (mapReady && mapInstanceRef.current) {
      if (clustererRef.current) {
        /* Удаляем предыдущий */
        mapInstanceRef.current.geoObjects.remove(clustererRef.current);
      }

      /* Инициализируем новый */
      clustererRef.current = initClusterer({
        shops,
        allItems,
        itemsCount,
        onPlacemarkClick: (item) => {
          onClickPvz(item);
          mapInstanceRef.current.setCenter([item.latitude, item.longitude]);
          mapInstanceRef.current.setZoom(18, {
            duration: 300,
          });
        },
      });

      /* Добавляем новый */
      mapInstanceRef.current.geoObjects.add(clustererRef.current);
    }
  }, [mapReady, shops, allItems, itemsCount]);

  const onClickPvz = useCallback((item: any) => {
    setTempActivePvz(item);
    setShowPvzDetails(true);
  }, []);

  const onClickSelectPvz = () => {
    setActivePvz(tempActivePvz);
    onStoreChange && onStoreChange(tempActivePvz?.address || '');
    onClose();
  };

  const onCloseDetails = () => {
    setActivePvz(null);
    setShowPvzDetails(false);
    setShowChooseCityPopup(false);
    setHideChooseCityControls(false);
    setShowBackArrow(false);
    if (isMd && didMount) {
      setSheetVisible(true);
    }
  };

  const hasPickUpItems = useMemo(
    () =>
      (pickupItems && pickupItems.length > 0) ||
      (shops && shops.length > 0 && shops.some((item) => item.type === 'pvz')),
    [pickupItems, shops]
  );

  const hasPostamatItems = useMemo(() => shops.some((shop) => shop.type === 'postamat'), [shops]);

  useEffect(() => {
    const refValue = scrollRef.current;
    const lockPaddingValue = window.innerWidth - document.body.offsetWidth + 'px';

    if (refValue && rootLenis) {
      rootLenis.stop();
      disableBodyScroll(refValue, {
        reserveScrollBarGap: true,
      });
      document.body.style.paddingRight = lockPaddingValue;
    }

    return () => {
      if (refValue && rootLenis) {
        enableBodyScroll(refValue);
        rootLenis.start();
        document.body.style.paddingRight = '0';
      }
    };
  }, []);

  const handleCityClick = () => {
    setShowChooseCityPopup(true);
    setHideChooseCityControls(true);
    setShowBackArrow(true);
    if (isMd && didMount) {
      setSheetVisible(false);
    }
  };

  const cityName = useCityStore((state) => state.cityName);

  useEffect(() => {
    if (cityName === 'unknown') {
      setShowChooseCityPopup(true);
      setHideChooseCityControls(true);
      if (isMd && didMount) {
        setSheetVisible(false);
      }
    }
  }, [cityName]);

  const handleCityChange = () => {
    setShowChooseCityPopup(false);
    setHideChooseCityControls(false);
    setShowBackArrow(false);
    if (isMd && didMount) {
      setSheetVisible(true);
    }
  };

  const minHeightBottom: number = useMediaValues(
    {
      0: 220,
      768: 220,
    },
    0
  );

  const minHeightBottomSheet = useMemo(() => {
    return Math.round(minHeightBottom * coefficientRem);
  }, [coefficientRem, minHeightBottom]);

  const offsetHeader = useMemo(() => {
    return Math.round(72 * coefficientRem);
  }, [coefficientRem]);

  const onChangeStateBottomSheet = useCallback(() => {
    const state =
      sheetRef.current?.height === minHeightBottomSheet || sheetRef.current?.height === 0 ? 'bottom' : 'top';
    setStateBottomSheet(state);
  }, [sheetRef, minHeightBottomSheet]);

  const onSpringStart = useCallback(async () => {
    requestAnimationFrame(() => {
      onChangeStateBottomSheet();
    });
  }, [onChangeStateBottomSheet]);

  const onSpringEnd = useCallback(async () => onChangeStateBottomSheet(), [sheetRef, onChangeStateBottomSheet]);

  const onShowBottomSheet = useCallback(() => {
    sheetRef.current?.snapTo(({ lastSnap, maxHeight }) => {
      return lastSnap === minHeightBottomSheet || lastSnap === null ? maxHeight : minHeightBottomSheet;
    });
  }, [sheetRef, minHeightBottomSheet]);

  const onShowBottomSheetToTop = useCallback(
    () => sheetRef.current?.snapTo(({ maxHeight }) => maxHeight),
    [sheetRef.current]
  );

  useEffect(() => {
    if (isMd && didMount) {
      return setSheetVisible(true);
    }
  }, [isMd, didMount]);

  const date = useMemo(() => getDateAfterDaysFormatted(tariff?.period_max || 1), [tariff?.period_max]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.5,
        ease: 'easeOut',
      }}
      className={cvaRoot()}
      data-lenis-prevent
    >
      <div className={cvaMapWrapper()}>
        {!mapReady ? <LoadingSpinner size={'lg'} /> : null}
        <div ref={mapRef} className={cvaMap()}></div>
        {!showPvzDetails && !showBackArrow ? (
          <Button
            className={cvaClose()}
            rootWrapperClassName={cvaCloseWrapper({ left: false })}
            onClick={() => onClose()}
            color={'white'}
            hoverEffect={'highlight'}
            disableInViewAnimation
          >
            <IconClose className={cvaCloseIcon()} />
          </Button>
        ) : (
          <>
            <Button
              className={cvaBack()}
              rootWrapperClassName={cvaCloseWrapper({ left: true })}
              onClick={() => onCloseDetails()}
              color={'white'}
              hoverEffect={'highlight'}
              disableInViewAnimation
            >
              <IconArrowLeft className={cvaBackIcon()} />
            </Button>

            {/* mobile only */}
            <Button
              className={cvaClose()}
              rootWrapperClassName={twMergeExt(cvaCloseWrapper({ left: false }), 'sm:hidden block')}
              onClick={() => onClose()}
              color={'white'}
              hoverEffect={'highlight'}
              disableInViewAnimation
            >
              <IconClose className={cvaCloseIcon()} />
            </Button>
          </>
        )}
        <div className={cvaZoom()}>
          <Button className={cvaZoomButton()} ref={zoomInRef} color={'white'} hoverEffect={'highlight'}>
            <IconPlus className={cvaZoomButtonIcon()} />
          </Button>
          <Button className={cvaZoomButton()} ref={zoomOutRef} color={'white'} hoverEffect={'highlight'}>
            <IconMinus className={cvaZoomButtonIcon()} />
          </Button>
        </div>
      </div>
      {showChooseCityPopup ? (
        <ChooseCity
          onClose={() => {}}
          hideOverlay
          hideControls={hideChooseCityControls}
          onCityChange={handleCityChange}
        />
      ) : (
        <div
          className={cvaAside({
            paddingBottom: showPvzDetails,
          })}
          ref={scrollRef}
        >
          <div className={cvaAsideScroll()}>
            {!showPvzDetails ? (
              <div className={cvaAsideHeader()}>
                <div className={cvaAsideHeaderTitle()}>
                  Пункты выдачи
                  <br />
                  {activeCity !== 'unknown' ? (
                    <>
                      в{' '}
                      <span className={cvaAsideHeaderActiveCity()} onClick={handleCityClick}>
                        {activeCity}
                      </span>
                    </>
                  ) : null}
                </div>
                <div className={cvaSearch()}>
                  <input
                    type="text"
                    className={cvaSearchInput()}
                    placeholder={'Поиск по адресу'}
                    value={addressSearchQuery}
                    onChange={(e) => setAddressSearchQuery(e.target.value)}
                  />
                  <IconSearch className={cvaSearchIcon()} />
                </div>
                <div
                  className={cvaTags({
                    hidden: ![hasPickUpItems, hasPostamatItems].every(Boolean),
                  })}
                >
                  <div
                    className={cvaTag({
                      active: pvzFilter === 'all',
                    })}
                    onClick={() => {
                      setPvzFilter('all');
                    }}
                  >
                    Все
                  </div>
                  {hasPickUpItems ? (
                    <div
                      className={cvaTag({
                        active: pvzFilter === 'pvz',
                      })}
                      onClick={() => {
                        setPvzFilter('pvz');
                      }}
                    >
                      Пункт выдачи
                    </div>
                  ) : null}
                  {hasPostamatItems ? (
                    <div
                      className={cvaTag({
                        active: pvzFilter === 'postamat',
                      })}
                      onClick={() => {
                        setPvzFilter('postamat');
                      }}
                    >
                      Постамат
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <PvzMapDetailsHeader
                address={tempActivePvz?.address || ''}
                workingHours={tempActivePvz?.work_time}
                phone={tempActivePvz?.phone || ''}
                howToGet={tempActivePvz?.trip_description || ''}
                deliveryPrice={`+ 450 ₽`}
              />
            )}

            {!showPvzDetails ? (
              !isShopsLoading ? (
                <div className={cvaAsideItems()}>
                  {searchedAddresses?.length ? (
                    searchedAddresses?.map((item) =>
                      item.type === pvzFilter || pvzFilter === 'all' ? (
                        <PvzMapItem
                          data={item}
                          key={item.code}
                          address={item.address}
                          icon={item.organization}
                          type={item.type}
                          date={date}
                          workingHours={item.work_time}
                          itemsCount={itemsCount}
                          allItems={allItems}
                          onClickPvz={onClickPvz}
                        />
                      ) : null
                    )
                  ) : addressSearchQuery.length === 0 ? (
                    shops
                      ?.filter((item) => item.type === pvzFilter || pvzFilter === 'all')
                      .sort((a, b) => a.address.localeCompare(b.address))
                      .map((item) => (
                        <PvzMapItem
                          data={item}
                          key={item.code}
                          address={item.address}
                          icon={item.organization}
                          type={item.type}
                          date={date}
                          workingHours={item.work_time}
                          itemsCount={itemsCount}
                          allItems={allItems}
                          onClickPvz={onClickPvz}
                        />
                      ))
                  ) : (
                    <div className="pt-2.4 pl-8 text-bodyM text-cBlack48">
                      К сожалению, подходящих пунктов выдачи не найдено
                    </div>
                  )}
                </div>
              ) : (
                <div className={cvaLoadingContainer()}>
                  <LoadingSpinner size={'md'} />
                </div>
              )
            ) : (
              <div className={cvaStoreDetails()}>
                <PvzMapDetailsBody date={date} pickupItems={defaultProduct ? defaultProduct : pickupItems} />
              </div>
            )}
          </div>
          {!defaultProduct && showPvzDetails ? (
            <div className={cvaAsideButtonWrapper()}>
              <Button
                type="button"
                className={cvaAsideButton()}
                rootWrapperClassName="w-full"
                animateWrapperClassName="w-full"
                color={'mediumGreen'}
                hoverEffect={'highlight'}
                size={'medium'}
                onClick={() => onClickSelectPvz()}
              >
                Выбрать ПВЗ
              </Button>
            </div>
          ) : null}
        </div>
      )}
      <BottomSheet
        ref={sheetRef}
        className={cvaSheet()}
        open={sheetVisible}
        snapPoints={({ maxHeight }) => [minHeightBottomSheet, maxHeight - offsetHeader]}
        onSpringStart={onSpringStart}
        onSpringEnd={onSpringEnd}
        blocking={false}
        header={
          <div
            className={cvaBottomSheetHeader({
              bottom: stateBottomSheet === 'bottom',
              showArrow: !showPvzDetails,
            })}
            onClick={onShowBottomSheet}
          />
        }
        data-lenis-prevent
      >
        <div className={cvaAsideScroll()}>
          {!showPvzDetails ? (
            <HeaderShops
              search={addressSearchQuery}
              onChangeSearch={setAddressSearchQuery}
              handleCityClick={handleCityClick}
              activeCity={activeCity}
              onShowBottomSheetToTop={onShowBottomSheetToTop}
              hasPickUpItems={hasPickUpItems}
              hasPostamatItems={hasPostamatItems}
              pvzFilter={pvzFilter}
              setPvzFilter={setPvzFilter}
            />
          ) : (
            <PvzMapDetailsHeader
              address={tempActivePvz?.address || ''}
              workingHours={tempActivePvz?.work_time}
              deliveryPrice={'+ 999 ₽'}
            />
          )}

          {!showPvzDetails ? (
            !isShopsLoading ? (
              <div className={cvaAsideItems()}>
                <PvzMapListItems
                  search={addressSearchQueryDebounced}
                  pvzFilter={pvzFilter}
                  shops={shops}
                  date={date}
                  itemsCount={itemsCount}
                  allItems={itemsCount}
                  onClickPvz={onClickPvz}
                />
              </div>
            ) : (
              <div className={cvaLoadingContainer()}>
                <LoadingSpinner size={'md'} />
              </div>
            )
          ) : (
            <div className={cvaStoreDetails()}>
              <PvzMapDetailsBody date={date} pickupItems={defaultProduct ? defaultProduct : pickupItems} />
            </div>
          )}
        </div>
        {!defaultProduct && showPvzDetails ? (
          <div className={cvaAsideButtonWrapper()}>
            <Button
              type="button"
              className={cvaAsideButton()}
              rootWrapperClassName="w-full"
              animateWrapperClassName="w-full"
              color={'mediumGreen'}
              size={'medium'}
              onClick={() => onClickSelectPvz()}
            >
              Выбрать ПВЗ
            </Button>
          </div>
        ) : null}
      </BottomSheet>
    </motion.div>
  );
};

export default PvzMap;
